"use client";

import type { Review } from "@workspace/types";
import { Button } from "@workspace/ui/components/button";
import { Checkbox } from "@workspace/ui/components/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Label } from "@workspace/ui/components/label";
import {
	RadioGroup,
	RadioGroupItem,
} from "@workspace/ui/components/radio-group";
import { Switch } from "@workspace/ui/components/switch";
import { toast } from "@workspace/ui/hooks/use-sonner-toast";
import { useEffect, useMemo, useState } from "react";
import { useUserPreferences } from "@/hooks/shared/use-user-preferences";
import { exportScheduleAsImage } from "@/lib/features/festival/export/schedule-export";
import { trackScheduleExport } from "@/lib/services/analytics";

const PHONE_SIZES = {
	"iphone-16-pro": { width: 1290, height: 2796, label: "iPhone 14, 15 & 16" },
} as const;

// Gradient presets using Radix dark palette (step 4-5 tones)
const GRADIENT_PRESETS = [
	{ name: "Violet", colors: ["#2f2567", "#3a2359", "#1f2c5c"] },
	{ name: "Ocean", colors: ["#1f2c5c", "#182b59", "#0c3540"] },
	{ name: "Rose", colors: ["#481a26", "#4a1530", "#3a2359"] },
	{ name: "Emerald", colors: ["#0c3540", "#0d3520", "#1f2c5c"] },
	{ name: "Sunset", colors: ["#3f2200", "#481a26", "#2f2567"] },
] as const;

type PhoneSize = keyof typeof PHONE_SIZES;

interface ExportSetModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onExportingChange?: (isExporting: boolean) => void;
	festivalName: string;
	festivalSlug: string;
	festivalId?: string;
	selectedDay: string;
	availableDays: string[];
	username?: string;
	userProfileImageUrl?: string;
	sortAscending?: boolean;
	calendarData?: Array<{
		id: string;
		artist: string;
		stage: string;
		time: string;
		startHour: number;
		duration: number;
		day: string;
		imageUrl?: string | null;
		status: {
			isInSchedule: boolean;
			sawLive: boolean;
			sawStream: boolean;
			mustSee: boolean;
		};
	}>;
	reviews?: Review[];
	stages?: Array<{
		id: string;
		name: string;
		position: number;
	}>;
	eventType?: "main_festival" | "pre_party" | "after_party";
	defaultShowArtistPhotos?: boolean;
}

export function ExportSetModal({
	open,
	onOpenChange,
	onExportingChange,
	festivalName,
	festivalSlug,
	festivalId,
	selectedDay,
	availableDays,
	username,
	userProfileImageUrl,
	sortAscending = true,
	calendarData = [],
	reviews = [],
	stages = [],
	eventType = "main_festival",
	defaultShowArtistPhotos = true,
}: ExportSetModalProps) {
	// Export always uses dark theme (background image is dark)
	const theme = "dark" as const;
	const [sortOrder, setSortOrder] = useState<"standard" | "inverse">(
		sortAscending ? "standard" : "inverse",
	);
	const [phoneSize] = useState<PhoneSize>("iphone-16-pro");
	const [isExporting, setIsExporting] = useState(false);
	const [selectedStages, setSelectedStages] = useState<string[]>([]);
	// Initialize with currently selected day from parent
	// This ensures the modal defaults to the day the user is currently viewing
	const [selectedExportDay, setSelectedExportDay] = useState<string>(
		selectedDay,
	);
	const [hasUserCleared, setHasUserCleared] = useState(false);
	const [showArtistPhotos, setShowArtistPhotos] = useState(defaultShowArtistPhotos);
	const [gradientColors, setGradientColors] = useState<string[]>([...GRADIENT_PRESETS[0].colors]);
	const userPreferences = useUserPreferences();

	// Fixed maximum stages limit
	const maxStages = 6;

	// Filter stages to only those with sets on the selected day
	const activeStages = useMemo(() => {
		const stagesWithSets = new Set(
			calendarData
				.filter((item) => item.day === selectedExportDay)
				.map((item) => item.stage),
		);
		return stages.filter((s) => stagesWithSets.has(s.name));
	}, [stages, calendarData, selectedExportDay]);

	// Initialize selected stages with appropriate default based on phone size
	useEffect(() => {
		if (activeStages.length > 0 && selectedStages.length === 0 && !hasUserCleared) {
			// Default to max stages for the selected phone size
			const defaultStageCount = Math.min(maxStages, activeStages.length);
			setSelectedStages(activeStages.slice(0, defaultStageCount).map((s) => s.name));
		}
	}, [activeStages, selectedStages.length, hasUserCleared]);

	// Adjust selected stages if phone size changes and we exceed the new maximum
	useEffect(() => {
		if (selectedStages.length > maxStages) {
			setSelectedStages((prev) => prev.slice(0, maxStages));
			toast({
				title: "Stage selection adjusted",
				description: `Reduced to ${maxStages} stages for ${PHONE_SIZES[phoneSize].label}`,
			});
		}
	}, [phoneSize, selectedStages.length]);

	// Close modal on Escape key
	useEffect(() => {
		if (!open) return;

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				onOpenChange(false);
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [open, onOpenChange]);

	// Update parent component's exporting state
	useEffect(() => {
		onExportingChange?.(isExporting);
	}, [isExporting, onExportingChange]);

	// Reset exporting state when modal closes - critical for preventing infinite spinner
	useEffect(() => {
		if (!open) {
			setIsExporting(false);
			// Also notify parent component immediately when modal closes
			onExportingChange?.(false);
		}
	}, [open, onExportingChange]);

	const handleStageToggle = (stageName: string) => {
		if (
			!selectedStages.includes(stageName) &&
			selectedStages.length >= maxStages
		) {
			// Show toast for maximum stages
			toast({
				title: "Maximum stages reached",
				description: `You can select up to ${maxStages} stages for ${PHONE_SIZES[phoneSize].label}`,
				variant: "destructive",
			});
			return;
		}

		setSelectedStages((prev) =>
			prev.includes(stageName)
				? prev.filter((s) => s !== stageName)
				: [...prev, stageName],
		);

		// Reset the clear flag when user manually selects a stage
		if (hasUserCleared) {
			setHasUserCleared(false);
		}
	};

	const handleDeselectAll = () => {
		setSelectedStages([]);
		setHasUserCleared(true);
	};

	const handleExport = async () => {
		try {
			setIsExporting(true);

			// Filter calendar data by selected day and stages
			const filteredCalendarData = calendarData.filter(
				(item) =>
					item.day === selectedExportDay && selectedStages.includes(item.stage),
			);

			await exportScheduleAsImage({
				festivalName,
				festivalSlug,
				selectedDay: selectedExportDay,
				username,
				userProfileImageUrl,
				theme,
				sortOrder,
				phoneSize: PHONE_SIZES[phoneSize],
				calendarData: filteredCalendarData,
				reviews,
				selectedStages,
				userPreferences,
				eventType,
				availableDays,
				showArtistPhotos,
				gradientColors,
			});

			// Track the export
			if (festivalId) {
				trackScheduleExport(
					festivalId,
					festivalName,
					filteredCalendarData.length,
				);
			}

			toast({
				title: "Schedule exported",
				description: "Your schedule has been downloaded as an image.",
			});

			// Reset the exporting state immediately before closing modal
			setIsExporting(false);
			// Small delay to ensure state propagates before modal closes
			setTimeout(() => {
				onOpenChange(false);
			}, 100);
		} catch (error) {
			console.error("❌ Web export error:", error);
			toast({
				title: "Export failed",
				description: "Unable to export schedule. Please try again.",
				variant: "destructive",
			});
			// Always reset exporting state on error
			setIsExporting(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[90vh] w-full max-w-[95%] overflow-auto p-0 sm:max-w-md">
				<DialogTitle className="sr-only">Export Schedule</DialogTitle>
				<DialogDescription className="sr-only">
					Export your schedule as an image for your phone wallpaper
				</DialogDescription>
				<DialogHeader className="px-6 pt-6 pb-0">
					<h2 className="font-semibold leading-none tracking-tight">
						Export Schedule
					</h2>
				</DialogHeader>

				<div className="space-y-4 px-6 py-6">
					{/* Day Selection */}
					{availableDays.length > 1 && (
						<div className="space-y-2">
							<Label>Day</Label>
							<RadioGroup
								value={selectedExportDay}
								onValueChange={(value) => {
									setSelectedExportDay(value);
									setSelectedStages([]);
									setHasUserCleared(false);
								}}
							>
								{availableDays.map((day) => (
									<div key={day} className="flex items-center space-x-2">
										<RadioGroupItem value={day} id={day} />
										<Label htmlFor={day} className="cursor-pointer font-normal capitalize">
											{day}
										</Label>
									</div>
								))}
							</RadioGroup>
						</div>
					)}

					{/* Stage Selection */}
					{activeStages.length > 0 && (
						<div className="space-y-2">
							<div className="flex items-center justify-between">
								<Label>
									Stages ({selectedStages.length} selected
									{maxStages && `, max ${maxStages}`})
								</Label>
								<Button
									variant="ghost"
									size="sm"
									onClick={handleDeselectAll}
									className="h-7 px-2 text-xs"
								>
									Clear
								</Button>
							</div>
							<div className="max-h-32 overflow-y-auto rounded-lg border bg-muted/10 p-2">
								<div className="space-y-2">
									{activeStages.map((stage) => (
										<div
											key={stage.id}
											className="flex w-full cursor-pointer items-center space-x-2 rounded p-1 text-left hover:bg-muted/20"
											onClick={() => handleStageToggle(stage.name)}
											role="button"
											tabIndex={0}
											onKeyDown={(e) => {
												if (e.key === "Enter" || e.key === " ") {
													e.preventDefault();
													handleStageToggle(stage.name);
												}
											}}
										>
											<Checkbox
												checked={selectedStages.includes(stage.name)}
												onCheckedChange={() => handleStageToggle(stage.name)}
												id={`stage-${stage.id}`}
												onClick={(e) => e.stopPropagation()}
											/>
											<Label
												htmlFor={`stage-${stage.id}`}
												className="cursor-pointer font-normal"
											>
												{stage.name}
											</Label>
										</div>
									))}
								</div>
							</div>
						</div>
					)}

					{/* Sort Order */}
					<div className="space-y-2">
						<Label>Sort Order</Label>
						<RadioGroup
							value={sortOrder}
							onValueChange={(value) =>
								setSortOrder(value as "standard" | "inverse")
							}
						>
							<div className="flex items-center space-x-2">
								<RadioGroupItem value="inverse" id="inverse" />
								<Label htmlFor="inverse" className="cursor-pointer font-normal">
									Last set first
								</Label>
							</div>
							<div className="flex items-center space-x-2">
								<RadioGroupItem value="standard" id="standard" />
								<Label
									htmlFor="standard"
									className="cursor-pointer font-normal"
								>
									First set first
								</Label>
							</div>
						</RadioGroup>
					</div>

					{/* Artist Photos Toggle */}
					<div className="flex items-center justify-between">
						<Label htmlFor="export-artist-photos" className="cursor-pointer font-normal">
							Show artist photos
						</Label>
						<Switch
							id="export-artist-photos"
							checked={showArtistPhotos}
							onCheckedChange={setShowArtistPhotos}
						/>
					</div>

					{/* Gradient Theme */}
					<div className="space-y-2">
						<Label>Background</Label>
						<div className="flex items-center gap-2">
							{GRADIENT_PRESETS.map((preset) => (
								<button
									key={preset.name}
									type="button"
									onClick={() => setGradientColors([...preset.colors])}
									className={`size-9 rounded-full border-2 transition-all ${
										gradientColors.join() === preset.colors.join()
											? "border-white shadow-[0_0_0_2px_rgba(255,255,255,0.25)]"
											: "border-border hover:border-muted-foreground"
									}`}
									style={{
										background: `linear-gradient(135deg, ${preset.colors[0]}, ${preset.colors[1]}, ${preset.colors[2]})`,
									}}
									title={preset.name}
								/>
							))}
						</div>
					</div>
				</div>

				<DialogFooter className="px-6 pt-0 pb-6">
					<div className="flex w-full justify-end gap-3">
						<Button
							variant="ghost"
							onClick={() => onOpenChange(false)}
							disabled={isExporting}
						>
							Cancel
						</Button>
						<Button
							onClick={handleExport}
							disabled={isExporting || selectedStages.length === 0}
						>
							{isExporting ? "Exporting..." : "Export"}
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
